import { BreadcrumbProps, Classes } from "@blueprintjs/core";
import { useSetPlatformBreadcrumbs } from "infrastructure/platform/PlatformShellContext";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { segmentTrackToggleSidebar } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    useScenarioMetadata,
    useTrackedProject
} from "in_queue/contexts/ProjectDataContext";
import { FuelTypeIcon } from "in_queue/icons/fuelType/FuelTypeIcon";
import { getClusterLabel } from "in_queue/types/clusterType";
import { getProjectDisplayName } from "in_queue/types/projectType";
import { useEffect } from "react";
import { useMatch } from "react-router-dom";
import { BulkScenarioUploadLabel } from "../BulkScenarioUpload/bulkScenarioUploadLabels";
import {
    BULK_SCENARIO_UPLOAD_PATH,
    ScenarioBuilderLabel
} from "../ScenarioBuilder/scenarioBuilderParams";
import css from "./Breadcrumbs.module.scss";
import { TextLinkButton } from "./TextLinkButton";

export const InQueueBreadcrumbs: React.FC<{
    isSidebarOpen: boolean;
    onOpenSidebar: () => void;
}> = ({ isSidebarOpen, onOpenSidebar }) => {
    const setBreadcrumbs = useSetPlatformBreadcrumbs();

    const clusterMatch = useMatch(`/${IN_QUEUE_PATH}/:clusterSlug`);
    const projectMatch = useMatch(`/${IN_QUEUE_PATH}/:clusterSlug/:projectId`);
    const scenarioIdMatch = useMatch(
        `/${IN_QUEUE_PATH}/:clusterSlug/:projectId/scenario/:scenarioId`
    );
    const newScenarioMatch = useMatch(
        `/${IN_QUEUE_PATH}/:clusterSlug/:projectId/scenario/new`
    );
    const editScenarioMatch = useMatch(
        `/${IN_QUEUE_PATH}/:clusterSlug/:projectId/scenario/:scenarioId/edit`
    );
    const bulkUploadMatch = useMatch(
        `/${IN_QUEUE_PATH}/:clusterSlug/:projectId/scenario/${BULK_SCENARIO_UPLOAD_PATH}`
    );
    const builderMatch =
        newScenarioMatch ?? editScenarioMatch ?? bulkUploadMatch;
    const scenarioMatch = builderMatch != null ? null : scenarioIdMatch;

    const cluster = useCluster();

    const scenario = useScenarioMetadata(
        scenarioMatch?.params.projectId,
        scenarioMatch?.params.scenarioId
    );

    const projectIdFromRoute =
        projectMatch?.params.projectId ??
        scenarioMatch?.params.projectId ??
        builderMatch?.params.projectId;
    const project = useTrackedProject({
        projectId: projectIdFromRoute,
        phase: cluster.studyPhase
    });
    const projectLabel =
        project && project !== "loading"
            ? getProjectDisplayName(project)
            : projectIdFromRoute
            ? projectIdFromRoute
            : undefined;

    useEffect(() => {
        const clusterLabel = getClusterLabel(cluster);
        const projectFuelType =
            project && project !== "loading" ? project.fuelType : undefined;
        const projectIcon = projectFuelType ? (
            <span className={css["icon-wrapper"]}>
                <FuelTypeIcon fuelType={projectFuelType} />
            </span>
        ) : undefined;

        const breadcrumbs: BreadcrumbProps[] = [
            {
                text: (
                    <TextLinkButton
                        text="Select cluster"
                        onClick={() => {
                            if (!isSidebarOpen) {
                                onOpenSidebar();
                                segmentTrackToggleSidebar({
                                    isOpen: true,
                                    source: "navbar"
                                });
                            }
                        }}
                    />
                )
            }
        ];

        if (clusterMatch != null) {
            breadcrumbs.push({
                icon: "folder-close",
                href: `/${IN_QUEUE_PATH}/${cluster.slug}`,
                text: clusterLabel,
                current: true
            });
        }
        if (projectMatch != null) {
            breadcrumbs.push({
                icon: "folder-close",
                href: `/${IN_QUEUE_PATH}/${cluster.slug}`,
                text: clusterLabel
            });
            const projectId = projectMatch.params.projectId;
            breadcrumbs.push({
                icon: projectIcon,
                href: `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`,
                text: projectLabel ?? `Project ${projectId}`,
                current: true
            });
        }
        if (scenarioMatch != null) {
            breadcrumbs.push({
                icon: "folder-close",
                href: `/${IN_QUEUE_PATH}/${cluster.slug}`,
                text: clusterLabel
            });

            const projectId = scenarioMatch.params.projectId;
            breadcrumbs.push({
                icon: projectIcon,
                href: `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`,
                text: projectLabel ?? `Project ${projectId}`
            });

            let scenarioText;
            if (scenario === "loading" || scenario === undefined) {
                scenarioText = (
                    <>
                        Scenario:&nbsp;{" "}
                        <span className={Classes.SKELETON}>scenario title</span>
                    </>
                );
            } else {
                scenarioText = `Scenario: ${scenario.title}`;
            }
            const scenarioId = scenarioMatch.params.scenarioId;
            breadcrumbs.push({
                href: `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}/scenario/${scenarioId}`,
                text: scenarioText,
                current: true
            });
        }

        if (builderMatch != null) {
            breadcrumbs.push({
                icon: "folder-close",
                href: `/${IN_QUEUE_PATH}/${cluster.slug}`,
                text: clusterLabel
            });
            const projectId = builderMatch.params.projectId;
            breadcrumbs.push({
                icon: projectIcon,
                href: `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`,
                text: projectLabel ?? `Project ${projectId}`
            });
            breadcrumbs.push({
                text:
                    editScenarioMatch != null
                        ? ScenarioBuilderLabel.EditScenario
                        : bulkUploadMatch != null
                        ? BulkScenarioUploadLabel.PageTitle
                        : ScenarioBuilderLabel.NewScenario,
                current: true
            });
        }

        setBreadcrumbs(breadcrumbs);
        return () => setBreadcrumbs([]);
    }, [
        setBreadcrumbs,
        isSidebarOpen,
        onOpenSidebar,
        clusterMatch,
        projectMatch,
        scenarioMatch,
        builderMatch,
        editScenarioMatch,
        bulkUploadMatch,
        cluster,
        scenario,
        project,
        projectLabel
    ]);

    return null;
};
